import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Alert, Chip,
  CircularProgress, Menu, MenuItem, TextField, useMediaQuery, useTheme
} from '@mui/material';
import { Check, Refresh, HourglassEmpty, Cancel, RadioButtonUnchecked, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { format, addDays, subDays } from 'date-fns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

const VL_COLORS = [
  { light: '#fff8e1', dark: '#f9a825' }, { light: '#e3f2fd', dark: '#1976d2' },
  { light: '#e8f5e9', dark: '#388e3c' }, { light: '#f3e5f5', dark: '#7b1fa2' },
  { light: '#eceff1', dark: '#546e7a' }, { light: '#fce4ec', dark: '#c2185b' },
  { light: '#e0f7fa', dark: '#0097a7' }
];

const vlColorCache = {};
let colorIndex = 2;

const getVLColors = (vlName) => {
  if (!vlName) return { light: '#f5f5f5', dark: '#9e9e9e' };
  const name = vlName.toLowerCase().trim();
  if (name === 'openers' || name === 'op') return VL_COLORS[0];
  if (name === 'op+null' || name === 'opnull' || name.includes('op+null')) return VL_COLORS[1];
  if (vlColorCache[name]) return vlColorCache[name];
  const colors = VL_COLORS[colorIndex % VL_COLORS.length];
  vlColorCache[name] = colors;
  colorIndex++;
  return colors;
};

function DemAnalytics() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [localVolumes, setLocalVolumes] = useState({});
  const [volumeTimeouts, setVolumeTimeouts] = useState({});
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [statusMenuEntryId, setStatusMenuEntryId] = useState(null);

  useEffect(() => { fetchAnalytics(); }, [selectedDate]);
  useEffect(() => { const interval = setInterval(() => { fetchAnalytics(); }, 30000); return () => clearInterval(interval); }, [selectedDate]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/admin/dem/analytics/daily', { params: { date: format(selectedDate, 'yyyy-MM-dd') } });
      setSends(res.data.data || []);
      setLocalVolumes({});
    } catch (error) { console.error('Error:', error); setMessage({ type: 'error', text: 'Failed to load analytics' }); }
    finally { setLoading(false); }
  };

  const handleVolumeChange = (entryId, value) => {
    setLocalVolumes(prev => ({ ...prev, [entryId]: value }));
    if (volumeTimeouts[entryId]) clearTimeout(volumeTimeouts[entryId]);
    const timeout = setTimeout(async () => {
      try { await axios.put(`/admin/dem/calendar/${entryId}/volume`, { volume: parseInt(value) || 0 }); }
      catch (error) { setMessage({ type: 'error', text: 'Error updating volume' }); }
    }, 1000);
    setVolumeTimeouts(prev => ({ ...prev, [entryId]: timeout }));
  };

  const handleOpenStatusMenu = (event, entryId) => { setStatusMenuAnchor(event.currentTarget); setStatusMenuEntryId(entryId); };
  const handleCloseStatusMenu = () => { setStatusMenuAnchor(null); setStatusMenuEntryId(null); };

  const handleUpdateStatus = async (newStatus) => {
    try { await axios.put(`/admin/dem/calendar/${statusMenuEntryId}`, { status: newStatus }); setSends(prev => prev.map(s => s.id === statusMenuEntryId ? { ...s, status: newStatus } : s)); handleCloseStatusMenu(); }
    catch (error) { setMessage({ type: 'error', text: 'Error updating status' }); }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'planned': return <Check sx={{ color: '#4caf50', fontSize: 18 }} />;
      case 'paused': return <HourglassEmpty sx={{ color: '#ff9800', fontSize: 18 }} />;
      case 'aborted': return <Cancel sx={{ color: '#f44336', fontSize: 18 }} />;
      default: return <RadioButtonUnchecked sx={{ color: '#9e9e9e', fontSize: 18 }} />;
    }
  };

  const getVolumeForSend = (send) => localVolumes[send.id] !== undefined ? localVolumes[send.id] : send.volume || '';

  const calculateStats = (send) => {
    const volume = parseInt(localVolumes[send.id] !== undefined ? localVolumes[send.id] : send.volume) || 0;
    const leads = send.leads || 0;
    const cost = (volume * 0.0002);
    const revenue = parseFloat(send.revenue) || 0;
    const ecpm = volume > 0 ? (revenue / volume) * 1000 : 0;
    const profit = revenue - cost;
    return { cost: cost.toFixed(1), revenue: revenue.toFixed(1), ecpm: ecpm.toFixed(1), profit: profit.toFixed(1) };
  };

  const sortedSends = [...sends].sort((a, b) => {
    const partnerCompare = (a.partner_id || 0) - (b.partner_id || 0);
    if (partnerCompare !== 0) return partnerCompare;
    const dbCompare = (a.database_name || '').localeCompare(b.database_name || '');
    if (dbCompare !== 0) return dbCompare;
    return (a.send_time || '99:99').localeCompare(b.send_time || '99:99');
  });

   const totals = sortedSends.reduce((acc, s) => {
    const volume = parseInt(localVolumes[s.id] !== undefined ? localVolumes[s.id] : s.volume) || 0;
    const leads = s.leads || 0; const cost = volume * 0.0002; const revenue = parseFloat(s.revenue) || 0;
    const ecpm = volume > 0 ? (revenue / volume) * 1000 : 0;
    return { 
      volume: acc.volume + volume, 
      clicks: acc.clicks + (s.clicks || 0), 
      leads: acc.leads + leads, 
      cost: acc.cost + cost, 
      revenue: acc.revenue + revenue, 
      profit: acc.profit + (revenue - cost),
      opens: acc.opens + (s.maildome_unique_opens || 0),
      mdClicks: acc.mdClicks + (s.maildome_unique_clicks || 0),
      unsubs: acc.unsubs + (s.maildome_unsubscribes || 0),
      ecpmSum: acc.ecpmSum + ecpm,
      ecpmCount: acc.ecpmCount + (volume > 0 ? 1 : 0)
    };
  }, { volume: 0, clicks: 0, leads: 0, cost: 0, revenue: 0, profit: 0, opens: 0, mdClicks: 0, unsubs: 0, ecpmSum: 0, ecpmCount: 0 });

  const avgEcpm = totals.ecpmCount > 0 ? (totals.ecpmSum / totals.ecpmCount).toFixed(1) : '-';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>DEM Analytics</Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton onClick={() => setSelectedDate(subDays(selectedDate, 1))} size="small"><ChevronLeft /></IconButton>
            <DatePicker open={datePickerOpen} onOpen={() => setDatePickerOpen(true)} onClose={() => setDatePickerOpen(false)} value={selectedDate} onChange={(newDate) => { setSelectedDate(newDate); setDatePickerOpen(false); }} slotProps={{ textField: { size: 'small', sx: { width: { xs: 120, sm: 150 } } } }} />
            <IconButton onClick={() => setSelectedDate(addDays(selectedDate, 1))} size="small"><ChevronRight /></IconButton>
            <IconButton size="small" onClick={fetchAnalytics} title="Refresh"><Refresh sx={{ fontSize: 20 }} /></IconButton>
          </Box>
        </Box>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

        <Card>
          <CardContent sx={{ py: 1.5, px: { xs: 1, sm: 2 } }}>
            <Typography variant="subtitle1" fontWeight={600} mb={1.5} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
              {format(selectedDate, isMobile ? 'EEE, dd MMM' : 'EEEE, dd MMMM yyyy')} - {sortedSends.length} sends
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer sx={{ maxHeight: { xs: 400, sm: 600 } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ width: 40, p: 0.5 }}>St</TableCell>
                      <TableCell sx={{ width: isMobile ? 60 : 100, p: 0.5 }}>DB</TableCell>
                      {!isMobile && <TableCell sx={{ width: 45, p: 0.5 }}>Time</TableCell>}
                      <TableCell sx={{ p: 0.5 }}>Campaign</TableCell>
                      {!isMobile && <TableCell sx={{ width: 70, p: 0.5 }}>VL</TableCell>}
                      <TableCell align="right" sx={{ width: isMobile ? 70 : 90, p: 0.5 }}>Vol</TableCell>
                      {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>Cost</TableCell>}
                      {!isMobile && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>Clk</TableCell>}
                      <TableCell align="right" sx={{ width: 45, p: 0.5 }}>Ld</TableCell>
                      <TableCell align="right" sx={{ width: 60, p: 0.5 }}>Rev</TableCell>
                      {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>eCPM</TableCell>}
                      <TableCell align="right" sx={{ width: 60, p: 0.5 }}>Profit</TableCell>
                      {!isTablet && <TableCell align="right" sx={{ width: 55, p: 0.5 }}>Opens</TableCell>}
                      {!isTablet && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>MdClk</TableCell>}
                      {!isTablet && <TableCell align="right" sx={{ width: 50, p: 0.5 }}>Unsub</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedSends.length === 0 ? (
                      <TableRow><TableCell colSpan={isMobile ? 6 : 15} align="center" sx={{ py: 3 }}><Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>No sends scheduled</Typography></TableCell></TableRow>
                    ) : (
                      <>
                        {sortedSends.map((send, index) => {
                          const colors = getVLColors(send.virtual_list_name);
                          const prevSend = index > 0 ? sortedSends[index - 1] : null;
                          const showDatabase = !prevSend || prevSend.database_name !== send.database_name;
                          const stats = calculateStats(send);
                          return (
                            <TableRow key={send.id} hover sx={{ bgcolor: showDatabase ? '#fafafa' : 'inherit' }}>
                              <TableCell align="center" sx={{ p: 0.25 }}><IconButton size="small" onClick={(e) => handleOpenStatusMenu(e, send.id)} sx={{ p: 0.25 }}>{getStatusIcon(send.status)}</IconButton></TableCell>
                              <TableCell sx={{ p: 0.5, fontWeight: showDatabase ? 600 : 400, fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>{showDatabase ? (send.database_name?.substring(0, isMobile ? 8 : 12) || '') : ''}</TableCell>
                              {!isMobile && <TableCell sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.send_time?.substring(0, 5) || '-'}</TableCell>}
                              <TableCell sx={{ p: 0.5 }}><Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 600, maxWidth: { xs: 80, sm: 150 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{send.campaign_name}</Typography></TableCell>
                              {!isMobile && <TableCell sx={{ p: 0.5 }}><Chip label={send.virtual_list_name || '-'} size="small" sx={{ bgcolor: colors.light, fontSize: '0.6rem', height: 18 }} /></TableCell>}
                              <TableCell align="right" sx={{ p: 0.5 }}><TextField size="small" type="number" value={getVolumeForSend(send)} onChange={(e) => handleVolumeChange(send.id, e.target.value)} sx={{ width: isMobile ? 65 : 80 }} inputProps={{ style: { textAlign: 'right', padding: '3px 6px', fontSize: '0.75rem' } }} /></TableCell>
                              {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.cost}</TableCell>}
                              {!isMobile && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.clicks}</TableCell>}
                              <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{send.leads}</strong></TableCell>
                              <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.revenue}</TableCell>
                              {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{stats.ecpm}</TableCell>}
                              <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem', color: parseFloat(stats.profit) >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>{stats.profit}</TableCell>
                              {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unique_opens || '-'}</TableCell>}
                              {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unique_clicks || '-'}</TableCell>}
                              {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}>{send.maildome_unsubscribes || '-'}</TableCell>}
                            </TableRow>
                          );
                        })}
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell colSpan={isMobile ? 4 : 5} align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>TOTAL</strong></TableCell>
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.volume.toLocaleString()}</strong></TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.cost.toFixed(1)}</strong></TableCell>}
                          {!isMobile && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.clicks}</strong></TableCell>}
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.leads}</strong></TableCell>
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.revenue.toFixed(1)}</strong></TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{avgEcpm}</strong></TableCell>}
                          <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem', color: totals.profit >= 0 ? 'success.main' : 'error.main' }}><strong>{totals.profit.toFixed(1)}</strong></TableCell>
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.opens || '-'}</strong></TableCell>}
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.mdClicks || '-'}</strong></TableCell>}
                          {!isTablet && <TableCell align="right" sx={{ p: 0.5, fontSize: '0.7rem' }}><strong>{totals.unsubs || '-'}</strong></TableCell>}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={handleCloseStatusMenu}>
          <MenuItem onClick={() => handleUpdateStatus('pending')}><RadioButtonUnchecked sx={{ color: '#9e9e9e', fontSize: 18, mr: 1 }} /> Pending</MenuItem>
          <MenuItem onClick={() => handleUpdateStatus('planned')}><Check sx={{ color: '#4caf50', fontSize: 18, mr: 1 }} /> Planned</MenuItem>
          <MenuItem onClick={() => handleUpdateStatus('paused')}><HourglassEmpty sx={{ color: '#ff9800', fontSize: 18, mr: 1 }} /> Paused</MenuItem>
          <MenuItem onClick={() => handleUpdateStatus('aborted')}><Cancel sx={{ color: '#f44336', fontSize: 18, mr: 1 }} /> Aborted</MenuItem>
        </Menu>
      </Box>
    </LocalizationProvider>
  );
}

export default DemAnalytics;
